import React, { useEffect, useState } from 'react'
import Axios from 'axios'

import Toast from './Toast'

import { TrashIcon } from '@heroicons/react/24/solid'

import { deleteSearch } from '../../../services/httpService'

import { baseURL } from '../../../config'
import styles from './UserSearches.module.css'

const titleCase = value => {
	if (!value || typeof value !== 'string') return value
	return value
		.toLowerCase()
		.split(' ')
		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ')
}

const hasValue = value => {
	if (Array.isArray(value)) return value.length > 0
	return value !== undefined && value !== null && value !== ''
}

const firstValue = (...values) => values.find(hasValue)

const formatList = (items, limit = 2) => {
	if (!Array.isArray(items) || items.length === 0) return null

	const labels = items
		.map(item => {
			if (typeof item === 'string') return item
			return item?.name || item?.label || item?.value
		})
		.filter(Boolean)

	if (labels.length === 0) return null

	const visible = labels.slice(0, limit).join(', ')
	const hiddenCount = labels.length - limit
	return hiddenCount > 0 ? `${visible} +${hiddenCount}` : visible
}

const formatCityState = (city, state) => {
	if (!city) return null
	return state ? `${city}, ${state}` : city
}

const getSearchSummaryParts = search => {
	const filters = search.filters || {}
	const pickupLocations = firstValue(filters.pickupLocations, search.pickupLocations)
	const dropoffLocations = firstValue(filters.dropoffLocations, search.dropoffLocations)
	const destinationStates = firstValue(filters.destinationStates, search.destinationStates)
	const destinationRegions = firstValue(filters.destinationRegions, search.destinationRegions)

	const destination =
		formatList(dropoffLocations) ||
		(destinationStates?.length ? formatList(destinationStates, 4) : null) ||
		(destinationRegions?.length
			? formatList(destinationRegions.map(titleCase), 3)
			: null) ||
		formatCityState(filters.dropCity, filters.dropState)

	const origin =
		formatList(pickupLocations) ||
		formatCityState(filters.pickupCity, filters.pickupState)
	const equipment = firstValue(filters.equipType, search.unitType)

	return {
		route:
			origin && destination
				? `${origin} to ${destination}`
				: origin || destination || null,
		equipment: equipment ? titleCase(equipment) : null,
	}
}

function UserSearches({ isModalOpen, closeModal, setFilters, isDarkMode, setRefreshInterval }) {
	const [searches, setSearches] = useState([])
	const [loading, setLoading] = useState(false)
	const [showToast, setShowToast] = useState(false)
	const [error, setError] = useState('')
	const secondaryButtonClasses = `inline-flex h-8 items-center justify-center rounded-md border px-3 text-xs font-medium transition-colors ${
		isDarkMode
			? 'border-[#444c56] bg-[#2d333b] text-[#adbac7] hover:bg-[#444c56]'
			: 'border-[#d0d7de] bg-[#e6e8ea] text-[#1c2128] hover:bg-[#d0d7de]'
	}`
	const applyButtonClasses = `inline-flex h-7 shrink-0 items-center justify-center rounded-md border px-2.5 text-xs font-medium transition-colors ${
		isDarkMode
			? 'border-gray-700 text-gray-300 hover:border-gray-600 hover:bg-white/[0.06] hover:text-white'
			: 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900'
	}`
	const summaryValueClasses = isDarkMode ? 'text-gray-300' : 'text-gray-600'

	useEffect(() => {
		async function getSearchData() {
			setLoading(true)

			try {
				const data = await Axios.get(
					`${baseURL}/api/search/all?limit=${20}&skip=${0}`
				)
				setSearches(data.data.searches)
			} catch (err) {
				setError('Failed to load searches')
				setShowToast(true)
			} finally {
				setLoading(false)
			}
		}

		if (isModalOpen) {
			getSearchData()
		}
	}, [isModalOpen])

	const handleOutsideClick = event => {
		if (event.target.tagName.toLowerCase() === 'dialog') {
			closeModal()
		}
	}

	const applyFilters = search => {
		// Use saved filters if available, otherwise fallback to current filters
		const savedFilters = search.filters || {}

		// Determine destination type from saved search
		// Check both savedFilters (new format) and top-level search fields (legacy format)
		let destinationType = 'CITY'
		if (savedFilters.destinationRegions?.length > 0 || search.destinationRegions?.length > 0) {
			destinationType = 'REGION'
		} else if (savedFilters.destinationStates?.length > 0 || search.destinationStates?.length > 0) {
			destinationType = 'STATE'
		} else if (savedFilters.dropoffLocations?.length > 0 || search.dropoffLocations?.length > 0 || savedFilters.dropCity) {
			destinationType = 'CITY'
		}

		// Apply all saved filters, preserving the complete filter state
		const appliedFilters = {
			...savedFilters,
			destinationType: destinationType,
			paginateSize: 1,
		}

		setFilters(appliedFilters)

		if (search.refreshInterval && setRefreshInterval) {
			setRefreshInterval(search.refreshInterval)
		}

		closeModal()
	}

	const deleteSearchHandler = async id => {
		try {
			await deleteSearch(id)
			setSearches(searches => searches.filter(search => search._id !== id))
		} catch (err) {
			setError('Failed to delete search')
			setShowToast(true)
		}
	}

	if (showToast && error) {
		closeModal()
		return (
			<>
				<Toast message={error} error={true} />
			</>
		)
	}

	return (
		<div>
			{isModalOpen && (
				<dialog
					onClick={handleOutsideClick}
					className={`modal ${isModalOpen ? 'modal-open' : ''}`}
				>
					<form
						method="dialog"
						className={`modal-box ${styles['searches-modal-box-custom']}`}
						style={{
							backgroundColor: isDarkMode ? '#1c2126' : '#f5f5f7',
							transition: 'background-color 0.3s ease',
						}}
					>
						<h3
							className={`mb-8 font-bold text-lg ${
								isDarkMode ? 'text-gray-200' : 'text-gray-800'
							}`}
						>
							Saved Searches
						</h3>
						{loading ? (
							<h3
								className={`my-12 text-xl text-center ${
									isDarkMode ? 'text-gray-200' : 'text-gray-700'
								}`}
							>
								Loading...
							</h3>
						) : searches.length > 0 ? (
							searches.map((search, index) => {
								const summaryParts = getSearchSummaryParts(search)

								return (
									<div
										key={search._id || index}
										style={{
											backgroundColor: isDarkMode ? '#22272d' : '#ffffff',
											borderColor: isDarkMode ? '#444c56' : '#e5e7eb',
										}}
										className="rounded-lg flex items-start gap-3 border p-3 my-2"
									>
										<div className="min-w-0 flex-1">
											<p className={`truncate text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
												{search.searchName}
											</p>

											{(summaryParts.route || summaryParts.equipment) && (
												<div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
													{summaryParts.route && (
														<span className={`min-w-0 truncate font-medium ${summaryValueClasses}`}>
															{summaryParts.route}
														</span>
													)}
													{summaryParts.equipment && (
														<>
															{summaryParts.route && (
																<span className={isDarkMode ? 'text-gray-700' : 'text-gray-300'}>
																	·
																</span>
															)}
															<span className={`shrink-0 font-medium ${summaryValueClasses}`}>
																{summaryParts.equipment}
															</span>
														</>
													)}
												</div>
											)}
										</div>

										<div className="flex shrink-0 items-center gap-2">
											<button
												type="button"
												onClick={() => applyFilters(search)}
												className={applyButtonClasses}
											>
												Apply filters
											</button>

											<div
												className={`h-5 border-l ${
													isDarkMode ? 'border-gray-700' : 'border-gray-200'
												}`}
											></div>

											<button
												type="button"
												onClick={() => deleteSearchHandler(search._id)}
												className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-red-500 transition-colors hover:bg-red-500/10 hover:text-red-600"
												aria-label={`Delete ${search.searchName}`}
											>
												<TrashIcon className="h-4 w-4" />
											</button>
										</div>
									</div>
								)
							})
						) : (
							<h3
								className={`my-12 text-xl text-center ${
									isDarkMode ? 'text-gray-200' : 'text-gray-700'
								}`}
							>
								You don't have any searches
							</h3>
						)}
						<div className="modal-action">
							<button
								onClick={closeModal}
								type="button"
								className={secondaryButtonClasses}
							>
								Close
							</button>
						</div>
					</form>
				</dialog>
			)}
		</div>
	)
}

export default UserSearches
